import { Injectable, inject } from "@angular/core";
import { filter, Subject } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import Shepherd from "shepherd.js";
import { TOUR_START_DELAY } from "src/app/constants/business.constants";
import { FmsService } from "src/app/services/fms.service";
import { LocalStorageKey, LocalStorageService } from "src/app/services/local-storage.service";
import { getIsNotEmpty } from "src/app/utils/array";
import { offset } from "@floating-ui/dom";

export enum TourStepButtonType {
  Next = "next",
  Previous = "previous",
}

export interface TourStep {
  id: string;
  selector: string;
  title: string;
  text: string;
  position: Shepherd.Step.PopperPlacement;
  buttons?: TourStepButton[];
  offset?: number;
}

export interface TourStepButton {
  type: TourStepButtonType;
  text: string;
}

export interface TourManager {
  startTour(): void;
  stopTour(): void;
}

@Injectable({ providedIn: "root" })
export class TourService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly fmsService = inject(FmsService);

  private readonly steps: TourStep[] = [];
  private tour: Shepherd.Tour | null = null;

  private readonly nextTour$ = new Subject<void>();

  constructor() {
    this.nextTour$
      .pipe(
        debounceTime(TOUR_START_DELAY),
        map(() => this.steps),
        filter(getIsNotEmpty),
        filter(() => !this.tour?.isActive()),
      )
      .subscribe((steps) => {
        this.tour = new Shepherd.Tour({
          useModalOverlay: false,
          keyboardNavigation: true,
          exitOnEsc: true,
        });

        this.tour.addSteps(steps.map((step) => this.toStepOptions(step)));
        this.tour.on("complete", (): void => {
          const scrollY = window.scrollY;
          setTimeout(() => window.scrollTo({ top: scrollY }), 0);

          if (!this.getIsEveryStepCompleted(steps)) {
            this.nextTour$.next();
          }
        });

        this.tour.start();
      });
  }

  public startTour(steps: TourStep[]): void {
    steps.forEach((step) => {
      const stepIds = this.steps.map(({ id }) => id);

      if (!stepIds.includes(step.id)) {
        this.steps.push(step);
      }
    });

    this.nextTour$.next();
  }

  public stopTour(): void {
    this.tour?.cancel();
  }

  private toStepOptions(step: TourStep): Partial<Shepherd.Step.StepOptions> {
    const buttons = [
      {
        type: TourStepButtonType.Next,
        text: this.fmsService.getText("tour.button.next"),
      },
      ...(step.buttons || []),
    ];

    return {
      id: step.id,
      title: step.title,
      text: step.text,
      attachTo: {
        element: step.selector,
        on: step.position,
      },
      buttons: buttons.map((button) => ({
        text: button.text,
        secondary: getIsPreviousButton(button),
        action: (): void => {
          if (getIsPreviousButton(button)) {
            if (this.tour) {
              this.tour.back();
            }
          } else {
            if (this.tour) {
              this.tour.next();
            }
          }
        },
      })),
      showOn: (): boolean => !this.getIsStepCompleted(step.id) && getIsElementInDom(step.selector),
      when: {
        show: (): void => {
          this.setCompletedStep(step.id);
        },
      },
      floatingUIOptions: {
        middleware: [offset({ mainAxis: step.offset ?? 20 })],
      },
    };
  }

  private getCompletedSteps(): string[] {
    return this.localStorageService.get<string[]>(LocalStorageKey.CompletedTourSteps, []);
  }

  private setCompletedStep(stepId: string): void {
    this.localStorageService.set<string[]>(LocalStorageKey.CompletedTourSteps, [
      ...new Set([...this.getCompletedSteps(), stepId]),
    ]);
  }

  private getIsStepCompleted(stepId: string): boolean {
    return this.getCompletedSteps().includes(stepId);
  }

  private getIsEveryStepCompleted(steps: TourStep[]): boolean {
    return steps.every((step) => this.getIsStepCompleted(step.id));
  }
}

function getIsElementInDom(selector: string): boolean {
  return Boolean(document.querySelector(selector));
}

function getIsPreviousButton(button: TourStepButton): boolean {
  return button.type === TourStepButtonType.Previous;
}
